import React, { useState, useEffect } from 'react';
import './GestionEncuestasComponent.css';
import { GetAllPolls, GetAllPollsAdmin, UpdatePollStatus, DeletePoll, GetNonRespondents } from '../../Backend/ApiCalls';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { BottomNav } from '../BottomNav';
import { downloadNativeFile } from "../Descargas/downloadNativeFile";
import { API_BASE_URL } from '../../Backend/ApiCalls';
import {
  getAnswerText,
  paymentMethodLabel,
} from '../../utils/surveyExport';
import { FormattedText } from '../Common/FormattedText';
import { PaymentStatusBadge } from '../Common/PaymentStatusBadge';
import { formatCents } from '../../utils/formatMoney';
import { Notification } from '../NotificationLoginComponent';
import { AdminAddParticipantModal } from './AdminAddParticipantModal';
import { getActiveProfileToken } from '../../utils/profileStorage';
import { applyPollFilters, getQuestionKey, optionId, isFilterableQuestion } from './GestionEncuestasFilters';

// Importe EN PANTALLA (tarjetas/tabla admin): "5,00 €" en es-ES. Distinto de
// formatCurrency (utils/surveyExport.js), que sigue igual porque su salida
// ("5.00 EUR") se escribe tal cual dentro de los ficheros Excel/PDF
// exportados y no se debe tocar sin que se pida explícitamente.
const formatAmountDisplay = (amountCents) => formatCents(amountCents);

// True solo cuando corremos dentro de la app nativa (Capacitor). En el
// navegador web -aunque sea móvil- devuelve false.
const isNativeApp = () =>
  typeof window !== "undefined" &&
  Boolean(window.Capacitor?.isNativePlatform && window.Capacitor.isNativePlatform());

const slugForFile = (text) =>
  String(text || "encuesta")
    .normalize("NFD")
    // Quita los diacríticos combinantes (acentos, virgulilla, etc.)
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || "encuesta";

// Chips de filtro rápido (Parte 8): Todos / Pagados / Pendientes / Casal /
// Sin decidir / Infantiles / Juveniles / Adultos. Se exportan como funciones
// puras (sin depender de props/estado del componente) para poder testearlas
// directamente con jest, sin necesidad de renderizar nada ni de instalar
// @testing-library/react.
export const GestionEncuestasComponent = (props) => {
  const { user } = props;
  const [polls, setPolls] = useState([]);
  const [activeDetalles, setActiveDetalles] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('pendientes'); // 'pendientes' | 'activas' | 'finalizadas'
  // Filtro rápido (chips) de la lista de inscritos, por encuesta (Parte 8).
  // Solo filtra en el cliente sobre los datos ya cargados: no recarga nada.
  const [filtersByPoll, setFiltersByPoll] = useState({});
  const [mobileViewMode, setMobileViewMode] = useState(() => {
    const stored = sessionStorage.getItem('pollManagementViewMode');
    return stored === 'cards' || stored === 'table' ? stored : 'cards';
  });
  // Menú "Más acciones" (Parte 9): qué encuesta tiene el menú secundario
  // abierto (Despublicar/Publicar, Excel, PDF). Editar y Eliminar quedan
  // siempre visibles y fuera del menú.
  const [moreMenuOpenPollId, setMoreMenuOpenPollId] = useState(null);
  // Tarjeta de inscrito expandida en móvil (Parte 7): "Ver detalles" muestra
  // pagador, nº de operación y fechas completas sin saturar la vista principal.
  const [expandedRegistrationKey, setExpandedRegistrationKey] = useState(null);
  // Pendientes de contestar por encuesta: { [pollId]: { open, loading, data } }
  const [nonRespondents, setNonRespondents] = useState({});
  // Encuesta para la que está abierto el modal "Añadir participante"
  // (inscripción administrativa, Parte 2). null = cerrado.
  const [addParticipantPoll, setAddParticipantPoll] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [isErrorNoti, setIsErrorNoti] = useState(false);
  const navigate = useNavigate();

  const showNoti = (msg, isErr = false, ms = 5000) => {
    setNotificationMessage(msg);
    setIsErrorNoti(isErr);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), ms);
  };

  // Despliega/colapsa la lista de usuarios que no han contestado. Carga los
  // datos del backend la primera vez. Como el contenedor poll-detail fija su
  // altura con scrollHeight al abrirse, lo pasamos a 'auto' para que el
  // contenido extra no quede recortado.
  const toggleNonRespondents = async (pollId, detailId) => {
    const current = nonRespondents[pollId];
    const unlockDetailHeight = () => {
      requestAnimationFrame(() => {
        const detail = document.getElementById(detailId);
        if (detail && detail.clientHeight) detail.style.height = 'auto';
      });
    };

    if (current?.open) {
      setNonRespondents(prev => ({ ...prev, [pollId]: { ...current, open: false } }));
      return;
    }

    if (current?.data) {
      setNonRespondents(prev => ({ ...prev, [pollId]: { ...current, open: true } }));
      unlockDetailHeight();
      return;
    }

    setNonRespondents(prev => ({ ...prev, [pollId]: { open: true, loading: true, data: null } }));
    try {
      const { response, data } = await GetNonRespondents(pollId);
      setNonRespondents(prev => ({
        ...prev,
        [pollId]: {
          open: true,
          loading: false,
          data: response.ok ? data : { error: data?.Error || 'Error cargando datos' },
        },
      }));
    } catch (error) {
      setNonRespondents(prev => ({
        ...prev,
        [pollId]: { open: true, loading: false, data: { error: error.message } },
      }));
    }
    unlockDetailHeight();
  };

  const ageTypeLabel = (ageType) => {
    switch (Number(ageType)) {
      case 0: return 'Exento';
      case 1: return 'Infantil';
      case 2: return 'Juvenil';
      case 3: return 'Adulto';
      default: return 'Desconocido';
    }
  };

  const fetchPolls = async () => {
    try {
      let data = [];
      const adminRes = await GetAllPollsAdmin();

      if (adminRes.response?.ok && Array.isArray(adminRes.data)) {
        data = adminRes.data;
      } else {
        const fallbackRes = await GetAllPolls();
        data = Array.isArray(fallbackRes.data) ? fallbackRes.data : [];
      }

      data.forEach(poll => {
        if (typeof poll.questions === 'string') poll.questions = JSON.parse(poll.questions);
        if (typeof poll.voted === 'string') poll.voted = JSON.parse(poll.voted);
        if (typeof poll.paymentConfig === 'string') poll.paymentConfig = JSON.parse(poll.paymentConfig);
      });

      setPolls(data.map(poll => ({ ...poll, expanded: false })));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPolls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = (timeString || '').split(':');
    return `${hours || '00'}:${minutes || '00'}`;
  };

  // Formato corto para columnas de gestión de pagos: "18/05/26 14:25".
  // Acepta cualquier string que Date sepa parsear (ISO, MySQL DATETIME, etc.).
  const formatDateTimeShort = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  };

  // Nº de operación enmascarado ("…0304"): solo se ve completo al copiarlo
  // desde el detalle administrativo (Parte 7), para no exponerlo siempre.
  const maskOrderNumber = (orderNumber) => {
    const value = String(orderNumber || '');
    if (value.length <= 4) return value;
    return `…${value.slice(-4)}`;
  };

  const copyOrderNumber = async (orderNumber) => {
    try {
      await navigator.clipboard.writeText(String(orderNumber));
    } catch (error) {
      console.warn('No se pudo copiar el número de operación:', error);
    }
  };

  const onClick = (pollDetailId) => {
    const pollDetail = document.getElementById(pollDetailId);
    const currentActiveInfo = document.getElementById(activeDetalles);
    if (activeDetalles && activeDetalles !== pollDetailId && currentActiveInfo) {
      currentActiveInfo.style.height = '0px';
    }
    if (!pollDetail) return;
    if (!pollDetail.clientHeight) {
      pollDetail.style.height = `${pollDetail.scrollHeight}px`;
      setActiveDetalles(pollDetailId);
    } else {
      pollDetail.style.height = '0px';
      setActiveDetalles(null);
    }
  };

  const getToken = () => getActiveProfileToken();

  // ===== EXPORTS =====
  // Único origen del fichero para los tres contextos: el propio backend
  // (/Polls/:id/Export...), que genera el Excel/PDF real con
  // Content-Type/Content-Disposition correctos. Antes, en la app nativa, se
  // apuntaba a la página web /exportar-encuesta —que devuelve el HTML de la
  // SPA, no un fichero— y Filesystem.downloadFile() acababa "guardando" esa
  // página como si fuera el .xlsx/.pdf (ver informe, Parte 1/2: por eso el
  // fichero "guardado" no se podía abrir). Ahora:
  //   - App nativa: downloadNativeFile() descarga el fichero real y abre la
  //     hoja de compartir del sistema (o cae a la web si el plugin
  //     correspondiente no está disponible en este binario).
  //   - Web (escritorio o móvil): descarga directa del mismo endpoint.
  const buildExportUrl = (poll, kind) => {
    const path = kind === "xlsx" ? "ExportExcel" : kind === "pdf" ? "ExportPDF" : "ExportCobroCasal";
    return `${API_BASE_URL}/Polls/${encodeURIComponent(poll.id)}/${path}?token=${encodeURIComponent(getToken())}`;
  };

  const runExport = async (poll, kind, extension) => {
    const url = buildExportUrl(poll, kind);
    const fileName = `${slugForFile(poll.title)}${kind === "cobro" ? "_cobro_casal" : ""}.${extension}`;

    if (isNativeApp()) {
      const result = await downloadNativeFile({
        url,
        fileName,
        extension,
        onError: (err) => {
          showNoti(
            `No se ha podido descargar el archivo. ${err?.message || "Inténtalo de nuevo más tarde."}`,
            true,
            6000
          );
        },
      });

      if (result.mode === "fallback-web") {
        showNoti(
          "Esta versión de la app no tiene disponible el guardado directo; se ha abierto la descarga en el navegador.",
          false,
          6500
        );
      }
      // 'shared': el propio sistema ya mostró la hoja de compartir/guardar,
      // que ya es la confirmación — no hace falta un aviso adicional aquí
      // (Parte 12: nunca afirmar más de lo que se puede confirmar).
      return result;
    }

    // Web (escritorio o móvil): descarga directa, misma pestaña. El
    // servidor manda Content-Disposition: attachment, así que no navega
    // fuera de la app, solo dispara la descarga del navegador.
    window.location.href = url;
    return { mode: "browser" };
  };

  const exportToExcel = (poll) => runExport(poll, "xlsx", "xlsx");
  const exportToPDF = (poll) => runExport(poll, "pdf", "pdf");
  const exportToCobroCasal = (poll) => runExport(poll, "cobro", "pdf");

  const filterUsers = (users) => {
    if (!searchTerm) return users;
    return users.filter(user => {
      const fullName = `${user.name} ${user.surname}`.toLowerCase();
      const ageType =
        user.ageType === 0
          ? 'exento'
          : user.ageType === 1
            ? 'infantil'
            : user.ageType === 2
              ? 'juvenil'
              : user.ageType === 3
                ? 'adulto'
                : 'desconocido';

      const responses = user.responses
        ? user.responses
            .flatMap(response =>
              Array.isArray(response.response) ? response.response : [response.response]
            )
            .filter(r => typeof r === 'string')
            .map(r => r.toLowerCase())
        : [];

      return (
        fullName.includes(searchTerm.toLowerCase()) ||
        ageType.includes(searchTerm.toLowerCase()) ||
        responses.some(r => r.includes(searchTerm.toLowerCase()))
      );
    });
  };

  const updatePollFilters = (pollId, update) => setFiltersByPoll((prev) => ({ ...prev, [pollId]: { ...(prev[pollId] || {}), ...update } }));

  const now = new Date();
  const filteredPolls = polls.filter(poll => {
    const deadline = new Date(poll.date);
    const timeParts = (poll.time || '00:00:00').split(':');
    deadline.setHours(timeParts[0], timeParts[1], timeParts[2] || 0);

    const published = Number(poll.isPublished) === 1;
    if (selectedTab === 'pendientes') {
      return !published && deadline >= now;
    } else if (selectedTab === 'activas') {
      return published && deadline >= now;
    } else if (selectedTab === 'finalizadas') {
      return deadline < now;
    }
    return true;
  });

  const handleChangeStatus = async (pollId, newStatus) => {
    try {
      const { response } = await UpdatePollStatus(pollId, { isPublished: newStatus });
      if (response.ok) {
        setPolls(prevPolls =>
          prevPolls.map(poll =>
            poll.id === pollId ? { ...poll, isPublished: newStatus } : poll
          )
        );
      }
    } catch (error) {
      console.error('Error al actualizar el estado de la encuesta', error);
    }
  };

  const handleDeletePoll = async (pollId) => {
    try {
      const { response } = await DeletePoll(pollId);
      if (response.ok) {
        setPolls(prevPolls => prevPolls.filter(poll => poll.id !== pollId));
      }
    } catch (error) {
      console.error('Error al eliminar la encuesta', error);
    }
  };

  const handleDeletePollConfirm = (pollId, pollTitle) => {
    confirmAlert({
      title: 'Confirmación de eliminación',
      message: `¿Estás seguro que deseas eliminar la encuesta "${pollTitle}"?`,
      buttons: [
        { label: 'Sí', onClick: async () => { await handleDeletePoll(pollId); } },
        { label: 'No', onClick: () => {} }
      ]
    });
  };

  const pageTitle =
    selectedTab === 'pendientes'
      ? 'Encuestas Pendientes'
      : selectedTab === 'activas'
        ? 'Encuestas Activas'
        : selectedTab === 'finalizadas'
          ? 'Encuestas Finalizadas'
          : 'Gestión de Encuestas';

  const ageLabel = (ageType) =>
    ageType === 0 ? 'Exento'
    : ageType === 1 ? 'Infantil'
    : ageType === 2 ? 'Juvenil'
    : ageType === 3 ? 'Adulto'
    : 'Desconocido';

  const paymentConceptLabel = (row, fallback = '-') => {
    const items = Array.isArray(row?.paidItems) ? row.paidItems : [];
    if (items.length) {
      return items.map((item) => `${item.label} ${formatAmountDisplay(item.amountCents)}`).join(', ');
    }
    return row?.conceptLabel || fallback;
  };

  // Devuelve un timestamp para ordenar filas por fecha de inscripción.
  // Fallback a +Infinity para filas sin reservedAt (van al final).
  const reservedTimestamp = (row) => {
    const raw = row?.reservedAt || row?.registration?.reservedAt;
    if (!raw) return Number.POSITIVE_INFINITY;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  };

  const getAdminRows = (poll) => {
    const voted = Array.isArray(poll.voted) ? poll.voted : [];
    const registrations = Array.isArray(poll.paymentSummary?.registrations) ? poll.paymentSummary.registrations : [];
    const votedByDni = new Map(voted.map((row) => [String(row.dni), row]));

    if (!registrations.length) {
      return voted
        .map((row) => ({
          ...row,
          beneficiaryDni: row.dni,
          beneficiaryName: row.name,
          beneficiarySurname: row.surname,
          registration: null,
          responses: row.responses || [],
        }))
        // Orden cronológico ascendente: primer apuntado arriba.
        .sort((a, b) => reservedTimestamp(a) - reservedTimestamp(b));
    }

    return registrations
      .map((registration) => {
        const votedRow = votedByDni.get(String(registration.beneficiaryDni));
        return {
          ...registration,
          dni: registration.beneficiaryDni,
          name: votedRow?.name || registration.beneficiaryName,
          surname: votedRow?.surname || registration.beneficiarySurname,
          responses: votedRow?.responses || [],
        };
      })
      // Orden cronológico ascendente: el primer apuntado va arriba, el último abajo.
      .sort((a, b) => reservedTimestamp(a) - reservedTimestamp(b));
  };

  const computePollStats = (poll) => {
    const voted = Array.isArray(poll.voted) ? poll.voted : [];
    const total = voted.length;
    const paymentTotals = poll.paymentSummary?.totals || null;

    const ages = voted.reduce((acc, u) => {
      const lbl = ageLabel(u.ageType);
      acc[lbl] = (acc[lbl] || 0) + 1;
      return acc;
    }, {});

    const questionStats = (poll.questions || []).map((q) => {
      const counts = {};
      voted.forEach((u) => {
        const r = u.responses?.find(x => x.questionId === q.id)?.response;
        if (r == null) return;

        if (q.type === 'text') {
          const txt = Array.isArray(r) ? r.join(' ') : String(r ?? '');
          if (txt.trim().length > 0) counts.__textFilled = (counts.__textFilled || 0) + 1;
          return;
        }

        const ids = Array.isArray(r) ? r : [r];
        ids.forEach((id) => {
          const opt = (q.options || []).find(o => o.id === id);
          const label =
            typeof opt?.text === 'string'
              ? opt.text
              : (opt?.text?.text ?? String(id));
          counts[label] = (counts[label] || 0) + 1;
        });
      });

      return { id: q.id, text: q.text, type: q.type, counts };
    });

    return { total, ages, questionStats, paymentTotals };
  };

  return (
    <div className='polls-component'>
      {showNotification && (
        <Notification message={notificationMessage} type={isErrorNoti ? 'error' : 'success'} />
      )}

      {addParticipantPoll && (
        <AdminAddParticipantModal
          poll={addParticipantPoll}
          onClose={() => setAddParticipantPoll(null)}
          onSuccess={() => fetchPolls()}
          showNoti={showNoti}
        />
      )}

      <BottomNav I18n={props.I18n} user={user} />

      <div className="tabs">
        <button
          className={`btn-tab btn-tab-pending ${selectedTab === 'pendientes' ? 'active' : ''}`}
          onClick={() => setSelectedTab('pendientes')}
        >
          Encuestas Pendientes
        </button>
        <button
          className={`btn-tab btn-tab-active ${selectedTab === 'activas' ? 'active' : ''}`}
          onClick={() => setSelectedTab('activas')}
        >
          Encuestas Activas
        </button>
        <button
          className={`btn-tab btn-tab-finished ${selectedTab === 'finalizadas' ? 'active' : ''}`}
          onClick={() => setSelectedTab('finalizadas')}
        >
          Encuestas Finalizadas
        </button>
      </div>

      <div className='polls-gestion'>
        <div className='recipient'>
          <h1 className="titulo-page">{pageTitle}</h1>

          <div className='polls-gestion-container'>
            {filteredPolls.length > 0 ? (
              filteredPolls.map((poll, index) => (
                <div className="polls-container" key={poll.id ?? index}>
                  <button
                    type="button"
                    className='poll-title'
                    onClick={() => onClick(`poll-detail-${poll.id ?? index}`)}
                    aria-expanded={activeDetalles === `poll-detail-${poll.id ?? index}`}
                    aria-controls={`poll-detail-${poll.id ?? index}`}
                  >
                    <span>{poll.title}</span>
                    <span className="poll-title-chevron" aria-hidden="true">⌄</span>
                  </button>

                  <div id={`poll-detail-${poll.id ?? index}`} className='poll-detail'>
                    <div className="poll-block">
                      <h3 className="poll-block-title">Información</h3>
                      <FormattedText text={poll.description} collapsible={String(poll.description || '').length > 260} />
                      <dl className="poll-facts">
                        <div className="poll-fact"><dt>{props.I18n.Date}</dt><dd>{formatDate(poll.date)} · {formatTime(poll.time)}</dd></div>
                        {poll.location && <div className="poll-fact"><dt>{props.I18n.Place}</dt><dd>{poll.location}</dd></div>}
                        <div className="poll-fact">
                          <dt>Estado</dt>
                          <dd>
                            <span className={`poll-status-chip ${Number(poll.isPublished) === 1 ? 'is-published' : 'is-pending'}`}>
                              {Number(poll.isPublished) === 1 ? 'Publicada' : 'Pendiente'}
                            </span>
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* === RESUMEN ADMINISTRATIVO (Parte 8) === */}
                    {poll.voted && poll.voted.length > 0 && (() => {
                      const stats = computePollStats(poll);
                      const totals = poll.paymentSummary?.totals || null;
                      const pendingOnlineCount = (stats.paymentTotals?.pendingBizumCount || 0) + (stats.paymentTotals?.pendingCardCount || 0);
                      const pendingOnlineAmount = (totals?.pendingBizumAmountCents || 0) + (totals?.pendingCardAmountCents || 0);

                      return (
                        <div className="poll-block">
                          <h3 className="poll-block-title">Resumen</h3>
                          <div className="admin-summary-grid">
                            <div className="admin-summary-card">
                              <span className="admin-summary-value">{stats.total}</span>
                              <span className="admin-summary-label">Inscripciones</span>
                            </div>
                            {totals && (
                              <>
                                <div className="admin-summary-card is-good">
                                  <span className="admin-summary-value">{(stats.paymentTotals?.paidBizumCount || 0) + (stats.paymentTotals?.paidCardCount || 0)}</span>
                                  <span className="admin-summary-label">Pago online</span>
                                </div>
                                <div className="admin-summary-card is-manual">
                                  <span className="admin-summary-value">{stats.paymentTotals?.pendingManualCount || 0}</span>
                                  <span className="admin-summary-label">Pago en casal</span>
                                </div>
                                <div className="admin-summary-card is-warn">
                                  <span className="admin-summary-value">{pendingOnlineCount + (stats.paymentTotals?.unpaidCount || 0)}</span>
                                  <span className="admin-summary-label">Pendientes</span>
                                </div>
                                <div className="admin-summary-card is-good">
                                  <span className="admin-summary-value">{formatAmountDisplay(totals.paidAmountCents)}</span>
                                  <span className="admin-summary-label">Recaudado online</span>
                                </div>
                                <div className="admin-summary-card is-warn">
                                  <span className="admin-summary-value">{formatAmountDisplay(pendingOnlineAmount)}</span>
                                  <span className="admin-summary-label">Pendiente de cobro online</span>
                                </div>
                              </>
                            )}
                          </div>

                          <div className="stat-row">
                            {poll.paymentSummary?.conceptLabel && (
                              <div className="stat-chip">Concepto: {poll.paymentSummary.conceptLabel}</div>
                            )}
                            {['Exento', 'Infantil', 'Juvenil', 'Adulto', 'Desconocido'].map((k) => (
                              stats.ages[k] ? <div key={k} className="stat-chip">{k}: {stats.ages[k]}</div> : null
                            ))}
                          </div>

                          {(stats.questionStats || []).map(qs => (
                            <div key={qs.id} className="stat-block">
                              <h4 className="stat-title">{qs.text}</h4>

                              {qs.type === 'text' ? (
                                <p className="stat-note">
                                  Respuestas de texto:{' '}
                                  <strong>{qs.counts.__textFilled || 0}</strong>
                                </p>
                              ) : (
                                <div className="stat-row">
                                  {Object.entries(qs.counts).map(([opt, c]) => (
                                    <div key={opt} className="stat-chip">{opt}: {c}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    {/* ===================== */}

                    {/* === PENDIENTES DE CONTESTAR === */}
                    <div className="non-respondents-block">
                      <button
                        type="button"
                        className="non-respondents-toggle"
                        onClick={() => toggleNonRespondents(poll.id, `poll-detail-${poll.id ?? index}`)}
                      >
                        {nonRespondents[poll.id]?.open ? '▾' : '▸'} Pendientes de contestar
                        {nonRespondents[poll.id]?.data?.pending
                          ? ` (${nonRespondents[poll.id].data.pending.length})`
                          : ''}
                      </button>

                      {nonRespondents[poll.id]?.open && (
                        <div className="non-respondents-content">
                          {nonRespondents[poll.id].loading && <p>Cargando...</p>}

                          {nonRespondents[poll.id].data?.error && (
                            <p className="non-respondents-error">{nonRespondents[poll.id].data.error}</p>
                          )}

                          {nonRespondents[poll.id].data?.pending && (
                            nonRespondents[poll.id].data.pending.length === 0 ? (
                              <p>Todo el censo ha contestado 🎉</p>
                            ) : (
                              <>
                                <p className="non-respondents-summary">
                                  Han contestado {nonRespondents[poll.id].data.answered} de{' '}
                                  {nonRespondents[poll.id].data.totalCensus} censados.
                                </p>
                                <ul className="non-respondents-list">
                                  {nonRespondents[poll.id].data.pending.map((u) => (
                                    <li key={u.dni || u.id}>
                                      {u.surname} {u.name}
                                      <span className="non-respondents-age"> · {ageTypeLabel(u.ageType)}</span>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    {/* =============================== */}

                    {/* Acciones administrativas (Parte 9/15): Editar y Añadir
                        participante son las dos acciones principales, en una
                        fila de 2 columnas (1 columna en pantallas muy
                        estrechas). Publicar/Despublicar, exportar y Eliminar
                        van dentro de "Más acciones", en fila propia debajo
                        para no desbordar en móvil. Eliminar queda separado y
                        en rojo dentro de ese menú, con la misma confirmación
                        de siempre. */}
                    <div className="poll-admin-toolbar">
                      <div className="poll-admin-actions-row">
                        <button
                          className="btn-edit"
                          onClick={() => navigate(`/editar-encuesta/${poll.id}`)}
                        >
                          Editar encuesta
                        </button>

                        {/* Inscripción administrativa (Parte 2): solo visible
                            aquí, en gestión de encuestas, que ya exige
                            isPollManager tanto en la ruta (AdminRoute
                            minRank=2) como en el backend de cada endpoint que
                            usa el modal. Un usuario normal nunca ve este
                            botón porque nunca llega a esta pantalla. */}
                        <button
                          type="button"
                          className="btn-add-participant"
                          onClick={() => setAddParticipantPoll(poll)}
                        >
                          Añadir participante
                        </button>
                      </div>

                      <div className="poll-more-actions">
                        <button
                          type="button"
                          className="btn-more-actions"
                          aria-haspopup="true"
                          aria-expanded={moreMenuOpenPollId === poll.id}
                          onClick={() => setMoreMenuOpenPollId((current) => (current === poll.id ? null : poll.id))}
                        >
                          ⋯ Más acciones
                        </button>

                        {moreMenuOpenPollId === poll.id && (
                          <div className="poll-more-actions-menu" role="menu">
                            {Number(poll.isPublished) === 1 ? (
                              <button
                                role="menuitem"
                                className="poll-more-actions-item"
                                onClick={() => { handleChangeStatus(poll.id, 0); setMoreMenuOpenPollId(null); }}
                              >
                                Despublicar
                              </button>
                            ) : (
                              <button
                                role="menuitem"
                                className="poll-more-actions-item"
                                onClick={() => { handleChangeStatus(poll.id, 1); setMoreMenuOpenPollId(null); }}
                              >
                                Publicar
                              </button>
                            )}

                            {getAdminRows(poll).length > 0 && (
                              <>
                                <button
                                  role="menuitem"
                                  className="poll-more-actions-item"
                                  onClick={() => { exportToExcel(poll); setMoreMenuOpenPollId(null); }}
                                >
                                  {props.I18n.ExportToExcel}
                                </button>
                                <button
                                  role="menuitem"
                                  className="poll-more-actions-item"
                                  onClick={() => { exportToPDF(poll); setMoreMenuOpenPollId(null); }}
                                >
                                  {props.I18n.ExportToPDF}
                                </button>
                                {/* Exportación adicional (Parte 8): no sustituye al PDF completo. */}
                                <button
                                  role="menuitem"
                                  className="poll-more-actions-item"
                                  onClick={() => { exportToCobroCasal(poll); setMoreMenuOpenPollId(null); }}
                                >
                                  Exportar lista de cobro
                                </button>
                              </>
                            )}

                            <button
                              role="menuitem"
                              className="poll-more-actions-item is-danger"
                              onClick={() => { setMoreMenuOpenPollId(null); handleDeletePollConfirm(poll.id, poll.title); }}
                            >
                              Eliminar encuesta
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {getAdminRows(poll).length > 0 ? (() => {
                      const allRows = getAdminRows(poll);
                      const pollFilters = filtersByPoll[poll.id] || { answers: {} };
                      const visibleRows = applyPollFilters(poll, filterUsers(allRows), pollFilters);
                      const firstQuestion = (poll.questions || [])[0];

                      return (
                        <div className='table-container'>
                          <h3>{props.I18n.TargetedUsers}</h3>

                          <div className="search-input-container">
                            <input
                              type="text"
                              placeholder="Buscar..."
                              className="search-input"
                              value={searchTerm}
                              onChange={e => setSearchTerm(e.target.value)}
                            />
                            <FaSearch className="search-input-icon" />
                          </div>


                          <div className="dynamic-filter-panel">
                            <div className="filter-group"><strong>Edad</strong>{[[null,'Todos'],[1,'Infantil'],[2,'Juvenil'],[3,'Adulto']].map(([value,label]) => <button key={label} type="button" className={`quick-filter-chip ${pollFilters.age === value ? 'is-active' : ''}`} onClick={() => updatePollFilters(poll.id, { age: value })}>{label}</button>)}</div>
                            {(poll.questions || []).map((question, index) => isFilterableQuestion(question) && <div className="filter-group" key={getQuestionKey(question,index)}><strong>{question.text}</strong><button type="button" className="quick-filter-chip" onClick={() => updatePollFilters(poll.id,{answers:{...(pollFilters.answers||{}),[getQuestionKey(question,index)]:null}})}>Todos</button>{question.options.map((option) => <button key={optionId(option)} type="button" className={`quick-filter-chip ${pollFilters.answers?.[getQuestionKey(question,index)] === optionId(option) ? 'is-active' : ''}`} onClick={() => updatePollFilters(poll.id,{answers:{...(pollFilters.answers||{}),[getQuestionKey(question,index)]:optionId(option)}})}>{typeof option.text === 'string' ? option.text : option.text?.text}</button>)}</div>)}
                            {poll.paymentSummary?.enabled && <div className="filter-group"><strong>Pago</strong>{[['paid','Pagados'],['pending_online','Pendiente online'],['pending_manual','Casal'],['unpaid','Sin decidir'],['free','Sin pago necesario']].map(([value,label]) => <button key={value} type="button" className={`quick-filter-chip ${pollFilters.payment === value ? 'is-active' : ''}`} onClick={() => updatePollFilters(poll.id,{payment:pollFilters.payment === value ? null:value})}>{label}</button>)}</div>}
                            {(pollFilters.age != null || pollFilters.payment || Object.values(pollFilters.answers || {}).some(Boolean)) && <button type="button" className="clear-poll-filters" onClick={() => updatePollFilters(poll.id,{age:null,payment:null,answers:{}})}>Limpiar filtros</button>}
                          </div>
                          <p className="registration-count-note">{visibleRows.length} de {allRows.length} participantes</p>

                          {/* ===== Móvil: tarjetas (Parte 7) ===== */}
                          <div className="admin-mobile-view-switcher">
                            <button type="button" className={mobileViewMode === 'cards' ? 'is-active' : ''} onClick={() => { setMobileViewMode('cards'); sessionStorage.setItem('pollManagementViewMode', 'cards'); }}>Tarjetas</button>
                            <button type="button" className={mobileViewMode === 'table' ? 'is-active' : ''} onClick={() => { setMobileViewMode('table'); sessionStorage.setItem('pollManagementViewMode', 'table'); }}>Tabla</button>
                          </div>
                          <div className={`admin-registration-cards ${mobileViewMode === 'cards' ? 'is-active' : ''}`}>
                            {visibleRows.map((user) => {
                              const dni = user.dni || user.beneficiaryDni;
                              const rowKey = `${poll.id}-${dni}`;
                              const isExpanded = expandedRegistrationKey === rowKey;
                              const ageType = user.ageType ?? user.registration?.ageType;
                              const firstResponse = firstQuestion
                                ? getAnswerText(firstQuestion, user.responses?.find(r => r.questionId === firstQuestion.id)?.response || [])
                                : '';

                              return (
                                <div className="admin-registration-card" key={rowKey}>
                                  <div className="admin-registration-card-top">
                                    <span className="admin-registration-name">{user.surname || user.beneficiarySurname} {user.name || user.beneficiaryName}</span>
                                    <span className="admin-registration-age">{ageLabel(ageType)}</span>
                                  </div>

                                  {poll.paymentSummary?.enabled && (
                                    <div className="admin-registration-status-row">
                                      <PaymentStatusBadge status={user.paymentStatus} method={user.paymentMethod} compact />
                                      <strong className="admin-registration-amount">{formatAmountDisplay(user.amountCents)}</strong>
                                    </div>
                                  )}

                                  {firstQuestion && firstResponse && (
                                    <p className="admin-registration-response">
                                      <strong>{firstQuestion.text}:</strong> {firstResponse}
                                    </p>
                                  )}

                                  <button
                                    type="button"
                                    className="admin-registration-toggle"
                                    aria-expanded={isExpanded}
                                    onClick={() => { setExpandedRegistrationKey(isExpanded ? null : rowKey); requestAnimationFrame(() => { const detail = document.getElementById(`poll-detail-${poll.id}`); if (detail?.clientHeight) detail.style.height = 'auto'; }); }}
                                  >
                                    {isExpanded ? 'Ocultar detalles' : 'Ver detalles'}
                                  </button>

                                  {isExpanded && (
                                    <div className="admin-registration-details">
                                      {poll.paymentSummary?.enabled && (
                                        <>
                                          <div className="admin-registration-detail-row">
                                            <span>Método</span>
                                            <span>{paymentMethodLabel(user.paymentMethod)}</span>
                                          </div>
                                          <div className="admin-registration-detail-row">
                                            <span>Pagador</span>
                                            <span>{user.payerSurname || ''} {user.payerName || ''}</span>
                                          </div>
                                          <div className="admin-registration-detail-row">
                                            <span>Concepto</span>
                                            <span>{paymentConceptLabel(user, poll.paymentSummary?.conceptLabel || '-')}</span>
                                          </div>
                                          <div className="admin-registration-detail-row">
                                            <span>Inscrito</span>
                                            <span>{formatDateTimeShort(user.reservedAt)}</span>
                                          </div>
                                          <div className="admin-registration-detail-row">
                                            <span>Pagado</span>
                                            <span>{user.paymentStatus === 'paid' ? formatDateTimeShort(user.paidAt) : '-'}</span>
                                          </div>
                                          {user.orderNumber && (
                                            <div className="admin-registration-detail-row">
                                              <span>N.º operación</span>
                                              <span className="admin-registration-order">
                                                {maskOrderNumber(user.orderNumber)}
                                                <button
                                                  type="button"
                                                  className="admin-registration-copy-btn"
                                                  onClick={() => copyOrderNumber(user.orderNumber)}
                                                  aria-label="Copiar número de operación completo"
                                                  title={`Copiar ${user.orderNumber}`}
                                                >
                                                  Copiar
                                                </button>
                                              </span>
                                            </div>
                                          )}
                                        </>
                                      )}

                                      {(poll.questions || []).map((question, qIndex) => {
                                        const userResponse = user.responses?.find(r => r.questionId === question.id)?.response || [];
                                        const answerText = getAnswerText(question, userResponse);
                                        if (!answerText) return null;
                                        return (
                                          <div className="admin-registration-detail-row" key={qIndex}>
                                            <span>{question.text}</span>
                                            <span>{answerText}</span>
                                          </div>
                                        );
                                      })}
                                      {/* A propósito NO se muestra "Inscrito por"/administrador aquí
                                          (ver informe, Parte 14): esa auditoría se sigue guardando en
                                          BD (createdByUserId/updatedByUserId en poll_registrations),
                                          solo se ha dejado de pintar en la interfaz. */}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* ===== Tablet/escritorio: tabla (Parte 7) ===== */}
                          <div className={`admin-table-scroll ${mobileViewMode === 'table' ? 'is-active' : ''}`}>
                            <table className="voted-table">
                              <thead>
                                <tr>
                                  <th>{props.I18n.Name}</th>
                                  <th>{props.I18n.Age}</th>
                                  {poll.paymentSummary?.enabled && <th>Pago</th>}
                                  {poll.paymentSummary?.enabled && <th>Método</th>}
                                  {poll.paymentSummary?.enabled && <th>Pagador</th>}
                                  {poll.paymentSummary?.enabled && <th>Concepto</th>}
                                  {poll.paymentSummary?.enabled && <th>Importe</th>}
                                  {poll.paymentSummary?.enabled && <th>Inscrito</th>}
                                  {poll.paymentSummary?.enabled && <th>Pagado</th>}
                                  {poll.paymentSummary?.enabled && <th>N.º operación</th>}
                                  {(poll.questions || []).map((question, qIndex) => (
                                    <th key={qIndex}>{question.text}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {visibleRows.map(user => (
                                  <tr key={user.dni || user.beneficiaryDni}>
                                    <td>{user.surname || user.beneficiarySurname} {user.name || user.beneficiaryName}</td>
                                    <td>{ageLabel(user.ageType ?? user.registration?.ageType)}</td>
                                    {poll.paymentSummary?.enabled && (
                                      <td><PaymentStatusBadge status={user.paymentStatus} method={user.paymentMethod} compact /></td>
                                    )}
                                    {poll.paymentSummary?.enabled && (
                                      <td>{paymentMethodLabel(user.paymentMethod)}</td>
                                    )}
                                    {poll.paymentSummary?.enabled && (
                                      <td>{user.payerSurname || ''} {user.payerName || ''}</td>
                                    )}
                                    {poll.paymentSummary?.enabled && (
                                      <td>{paymentConceptLabel(user, poll.paymentSummary?.conceptLabel || '-')}</td>
                                    )}
                                    {poll.paymentSummary?.enabled && (
                                      <td>{formatAmountDisplay(user.amountCents)}</td>
                                    )}
                                    {poll.paymentSummary?.enabled && (
                                      <td>{formatDateTimeShort(user.reservedAt)}</td>
                                    )}
                                    {poll.paymentSummary?.enabled && (
                                      <td>
                                        {user.paymentStatus === 'paid'
                                          ? formatDateTimeShort(user.paidAt)
                                          : '-'}
                                      </td>
                                    )}
                                    {poll.paymentSummary?.enabled && (
                                      <td className="op-number-cell">
                                        {user.orderNumber ? (
                                          <span
                                            title="Número de pedido / order que verás en el portal de Redsys (Consultas → Operaciones)"
                                            onClick={() => copyOrderNumber(user.orderNumber)}
                                          >
                                            {maskOrderNumber(user.orderNumber)}
                                          </span>
                                        ) : '-'}
                                      </td>
                                    )}
                                    {(poll.questions || []).map((question, qIndex) => {
                                      const userResponse = user.responses?.find(
                                        r => r.questionId === question.id
                                      )?.response || [];
                                      return (
                                        <td key={qIndex}>
                                          {getAnswerText(question, userResponse)}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })() : (
                      <p>{props.I18n.ThereAreNoRegisteredUsers}</p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p>No hay encuestas para esta categoría.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
